// Candado de edición por cadena integrada: las pantallas lo leen para ocultar o deshabilitar escrituras.
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useRetailerIntegrated } from '../api/useRetailerIntegration';

export interface IntegrationLockValue {
    locked: boolean;
    /** Tooltip de la acción deshabilitada; `undefined` sin candado. */
    reason: string | undefined;
}

const UNLOCKED: IntegrationLockValue = { locked: false, reason: undefined };

const IntegrationLockContext = createContext<IntegrationLockValue>(UNLOCKED);

export function useIntegrationLock(): IntegrationLockValue {
    return useContext(IntegrationLockContext);
}

/** Con la cadena ya en mano (Negocios). */
export function IntegrationLockProvider({
    integrated,
    children,
}: {
    integrated: boolean;
    children: ReactNode;
}) {
    const { t } = useTranslation('integration');
    const reason = t('lockedAction');
    const value = useMemo(
        () => (integrated ? { locked: true, reason } : UNLOCKED),
        [integrated, reason],
    );
    return (
        <IntegrationLockContext.Provider value={value}>{children}</IntegrationLockContext.Provider>
    );
}

/** Con solo el id de la cadena (Portal Admin): consulta si está integrada. */
export function RetailerIntegrationLock({
    retailerId,
    children,
}: {
    retailerId: string | null;
    children: ReactNode;
}) {
    const integrated = useRetailerIntegrated(retailerId);
    return <IntegrationLockProvider integrated={integrated}>{children}</IntegrationLockProvider>;
}

export type IntegrationNoticeKey =
    | 'lockedSellerData'
    | 'lockedProduct'
    | 'lockedPrice'
    | 'lockedMovements'
    | 'lockedNewProduct'
    | 'lockedProvision';

/** Aviso de una línea que solo aparece con el candado puesto. */
export function IntegrationLockNotice({
    message,
    active = true,
}: {
    message: IntegrationNoticeKey;
    /** Para avisos que dependen además del tipo de SKU. */
    active?: boolean;
}) {
    const { locked } = useIntegrationLock();
    if (!locked || !active) return null;
    return <LockNoticeText message={message} />;
}

function LockNoticeText({ message }: { message: IntegrationNoticeKey }) {
    const { t } = useTranslation('integration');
    return (
        <p role="note" className="text-[11px] text-muted-foreground">
            {t(message)}
        </p>
    );
}
